import { Tree, TreeNode, Checkbox } from "construct-ui";

/**
 * Tree Check Box Component
 */
export default function TreeCheckboxComponent() {

  /**
   * hashDataMap
   * 
   * Special properties
   * checkedState: -1 | 0 | 1
   *      -1 - Indeterminate
   *      0  - Unchecked
   *      1  - Checked
   */
  let hashDataMap = new Map();
  let isHashDataMapReady = false;
  let expandedMap;
  let data = [];
  let selectedParentCodes = [];
  let selectedChildCodes = [];
  let onChangeCallback = () => { };

  let createFlattenedHashMap = (_data) => {
    _data.map(item => {
      if (hashDataMap.has(item.code)) {
        return;
      }
      hashDataMap.set(item.code, { ...item, checkedState: 0 })
      if (item.children) {
        createFlattenedHashMap(item.children);
      }
    });
    isHashDataMapReady = true;
  }

  let updateCheckedStatusHashMap = () => {
    hashDataMap.forEach((v, k, m) => {
      if (v.checkedState !== 1 && selectedParentCodes.includes(k)) {
        selectAll(v);
      }
    })
  }

  let handleNodeExpand = (node) => {
    expandedMap.set(node.key, true);
  }

  let handleNodeCollapse = (node) => {
    expandedMap.delete(node.key);
  }

  let updateParentCheckedState = (code = null) => {

    if (code === null) {
      return;
    }

    let parentCode = hashDataMap.get(code).parentCode;
    let parentNode = hashDataMap.get(parentCode);

    if (!parentNode) {
      return;
    }

    let siblingCodes = parentNode.children.map(c => c.code)
    let hasChecked = false;
    let hasIndeterminate = false;
    let hasUnchecked = false;
    let isParentUpdated = false;

    for (let i = 0; i < siblingCodes.length; i++) {
      if (hashDataMap.get(siblingCodes[i]).checkedState === 1) {
        hasChecked = true;
      } else if (hashDataMap.get(siblingCodes[i]).checkedState === 0) {
        hasUnchecked = true;
      } else if (hashDataMap.get(siblingCodes[i]).checkedState === -1) {
        hasIndeterminate = true;
      }

      if (hasIndeterminate || (hasChecked && hasUnchecked)) {
        parentNode.checkedState = -1;
        isParentUpdated = true;
        break;
      }
    }

    if (!isParentUpdated) {
      parentNode.checkedState = hasIndeterminate ? -1 : hasChecked ? 1 : 0
    }

    // Update grandparent
    updateParentCheckedState(parentCode);
  }

  let selectAll = (item) => {
    // Add current item
    hashDataMap.get(item.code).checkedState = 1;
    // Add children recursively
    item.children?.map(child => selectAll(child));
    // Update parent
    updateParentCheckedState(item.code)
  }

  let deselectAll = (item) => {
    // Delete current item
    hashDataMap.get(item.code).checkedState = 0;
    // Add children recursively
    item.children?.map(child => deselectAll(child));
    // Update parent
    updateParentCheckedState(item.code)
  }

  let getAllSelectedNodes = (nodeList, isAncestorSelected = false) => {
    nodeList.map(node => {
      let isCurrentNodeSelected = false;
      if (hashDataMap.get(node.code).checkedState === 1) {
        if (!isAncestorSelected) {
          selectedParentCodes.push(node.code);
          isCurrentNodeSelected = true;
        }
        if (!node.children || node.children.length === 0) {
          selectedChildCodes.push(node.code);
        } else {
          getAllSelectedNodes(node.children, isCurrentNodeSelected || isAncestorSelected)
        }
      } else if (hashDataMap.get(node.code).checkedState === -1) {
        getAllSelectedNodes(node.children)
      }
    })
  }

  let getSelectedCodes = () => {
    // Reset selections
    selectedParentCodes = [];
    selectedChildCodes = [];

    getAllSelectedNodes(data);
  }

  let onChange = (e, item) => {
    if (e.target.checked) {
      selectAll(item);
    } else {
      deselectAll(item);
    }

    getSelectedCodes();
    onChangeCallback(selectedParentCodes, selectedChildCodes);
  }

  let isChecked = (code) => {
    return (hashDataMap.get(code)).checkedState === 1;
  }

  let isIndeterminate = (code) => {
    return (hashDataMap.get(code)).checkedState === -1;
  }

  let renderNode = (item) => {
    const { children = [], code, name } = item;
    const hasChildren = children.length > 0;
    const checked = isChecked(code);
    const indeterminate = isIndeterminate(code);

    return m(TreeNode, {
      children: children.map(child => renderNode(child)),
      hasCaret: hasChildren,
      label: m(Checkbox, {
        checked: checked,
        indeterminate: indeterminate,
        label: name,
        onchange: e => {
          onChange(e, item);
        }
      }),
      key: code,
      isExpanded: expandedMap.has(code),
      onExpand: handleNodeExpand,
      onCollapse: handleNodeCollapse,
    });
  }

  return {
    oninit: (vnode) => {
      data = vnode.attrs.data ?? [];

      if (vnode.attrs.onChange) {
        onChangeCallback = vnode.attrs.onChange;
      }

      // To manage expansion states
      expandedMap = new Map();
      if (vnode.attrs.isLevel1Expanded === true) {
        data.map(level1Item => expandedMap.set(level1Item.code, true));
      }

      // To manage checked states
      createFlattenedHashMap(data);
    },
    oncreate: (vnode) => {
      if (vnode.attrs.selectedCodes) {
        selectedParentCodes = vnode.attrs.selectedCodes;
        updateCheckedStatusHashMap();
        setTimeout(() => {
          m.redraw();
        }, 100)
      }
    },
    view: (vnode) => {
      return hashDataMap.size > 0 && m(Tree, {
        class: 'scs-tree ' + vnode.attrs.class,
        nodes: vnode.attrs.data.map(item => renderNode(item)),
        style: vnode.attrs.style,
      })
    }
  }
}